<template>
  <div>
    <a-modal
      v-model="visible"
      title="Edit Notice"
      :width="1000"
      :destroyOnClose="true"
      :maskClosable="false"
      class="notification-modal"
    >
      <template slot="footer">
        <div class="d-flex justify-content-end px-3 pb-2">
          <a-button key="back" @click="handleCancel" size="large" class="mr-2 rounded-pill">Cancel</a-button>
          <a-button key="submit" type="primary" :loading="loading" @click="handleEdit" size="large" class="rounded-pill px-4">
            <a-icon type="save" /> Update Notice
          </a-button>
        </div>
      </template>

      <a-form :form="form" layout="vertical" class="px-3">
        <div class="row">
          <!-- Left Column: Message Details -->
          <div class="col-md-7 pr-md-4 border-right">
            <div class="mb-4">
              <p class="section-title">Delivery Method</p>
              <div class="d-flex align-items-center justify-content-between">
                <a-checkbox-group
                  v-model="selectedTypes"
                  class="delivery-checkboxes"
                  disabled
                >
                  <a-checkbox value="push">App Push</a-checkbox>
                  <a-checkbox value="email">Email</a-checkbox>
                  <a-checkbox value="sms">SMS</a-checkbox>
                </a-checkbox-group>

                <div v-if="selectedTypes.includes('sms')" class="sms-balance-badge">
                  Balance: <span class="balance-count">{{ smsBalance }}</span>
                </div>
              </div>

              <div v-if="selectedTypes.includes('sms')" class="mt-3">
                <a-select v-model="selectedTemplate" placeholder="Choose SMS Template" style="width: 100%">
                  <a-select-option :value="0" disabled>Choose SMS Template</a-select-option>
                  <a-select-option v-for="item in templates" :key="item.id" :value="item.id">
                    {{ item.template_name }}
                  </a-select-option>
                </a-select>
              </div>
            </div>

            <a-form-item label="Subject" v-if="selectedTypes.includes('push') || selectedTypes.includes('email')">
              <a-input
                placeholder="Subject"
                v-decorator="['subject', { rules: [{ required: false }] }]"
                size="large"
              />
            </a-form-item>

            <a-form-item label="Message">
              <a-textarea
                placeholder="Type your message here..."
                :autoSize="{ minRows: 6, maxRows: 10 }"
                :disabled="isTextareaDisabled"
                v-decorator="['message', { rules: [{ required: true, message: 'Please input a message!' }] }]"
              />
            </a-form-item>

            <div class="mt-3">
              <a-upload
                v-decorator="['file']"
                :fileList="fileList"
                :multiple="false"
                :remove="handleRemove"
                :beforeUpload="beforeUpload"
                class="compact-upload"
              >
                <a-button size="large" class="attach-btn" v-if="!uploadDisabled">
                  <a-icon type="paper-clip" /> Attach File
                </a-button>
              </a-upload>
            </div>
          </div>

          <!-- Right Column: Target Audience (Read Only) -->
          <div class="col-md-5 pl-md-4">
            <div class="target-panel p-3">
              <p class="section-title">Target Audience</p>

              <!-- Students Target -->
              <div class="audience-card mb-3 opacity-75">
                <div class="d-flex align-items-center mb-2">
                  <a-checkbox v-model="stCheck" disabled class="mr-2"></a-checkbox>
                  <i class="fe fe-users mr-2 text-primary"></i>
                  <span class="font-weight-bold">Students Target</span>
                </div>

                <div v-if="stCheck" class="mt-2">
                   <div class="class-selection-area p-2 bg-white rounded border">
                    <div class="row">
                       <div class="col-12 mb-2">
                        <small class="text-muted font-weight-bold d-block">Session</small>
                        <span class="small font-weight-bold">{{ getSessionName(selectedSession) }}</span>
                      </div>
                      <div class="col-6 mb-2">
                        <small class="text-muted font-weight-bold d-block">Class</small>
                        <span class="small font-weight-bold">{{ getClassName(selectedClass) }}</span>
                      </div>
                      <div class="col-6 mb-2">
                        <small class="text-muted font-weight-bold d-block">Section</small>
                        <span class="small font-weight-bold">{{ getSectionName(selectedSection) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Teachers Target -->
              <div class="audience-card opacity-75">
                <div class="d-flex align-items-center mb-2">
                  <a-checkbox v-model="trCheck" disabled class="mr-2"></a-checkbox>
                  <i class="fe fe-briefcase mr-2 text-primary"></i>
                  <span class="font-weight-bold">Teachers Target</span>
                </div>
                <div v-if="trCheck" class="mt-1">
                   <span class="text-muted small">All Staff Targeted</span>
                </div>
              </div>

              <div class="mt-4 p-2 bg-light rounded text-center">
                 <small class="text-muted font-italic">Targeting options are disabled for existing notices.</small>
              </div>
            </div>
          </div>
        </div>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'
import { find, includes } from 'lodash'

export default {
  name: 'EditNotice',
  data: function() {
    return {
      visible: false,
      loading: false,
      form: this.$form.createForm(this),
      oldrecord: {},

      // Delivery
      selectedTypes: ['push'],
      smsBalance: 0,
      templates: [],
      selectedTemplate: 0,
      fileList: [],
      filesToDelete: [],

      // Audience (mostly fixed/read-only for edit)
      stCheck: false,
      trCheck: false,
      schoolSessions: [],
      schoolClasses: [],
      sections: [],
      selectedSession: undefined,
      selectedClass: 0,
      selectedSection: 0,

      classSpinning: false,
      sectionSpinning: false,
      uploadDisabled: false,
    }
  },
  mounted() {
    this.fetchInitialData()
    this.$eventBus.$on('openEditForm', this.showForm)
  },
  computed: {
    ...mapState(['settings', 'user']),
    currentSession() {
      return find(this.schoolSessions, ['is_current', 1])
    },
    isTextareaDisabled() {
      if (this.selectedTemplate !== 0) {
        const selected = this.templates.find(item => item.id === this.selectedTemplate)
        if (selected) {
          this.$nextTick(() => {
            this.form.setFieldsValue({ message: selected.template })
          })
        }
      }
      return false
    },
  },
  methods: {
    moment,
    includes,
    fetchInitialData() {
      this.$http.get('/api/session').then(({ data }) => {
        this.schoolSessions = data.data
        this.getClasses()
      })
      this.getTemplates()
    },
    showForm(record) {
      this.visible = true
      this.oldrecord = record
      this.getSmsBalance()

      this.selectedTypes = ['sms', 'push', 'email'].filter(key => record[key] === 1)
      this.stCheck = record.student === 1
      this.trCheck = record.teacher === 1
      this.selectedTemplate = record.sms_template_id || 0
      this.selectedSession = record.session_id || 0
      this.selectedClass = record.class_id || 0
      this.selectedSection = record.section_id || 0

      if (record.class_id) this.getClassSections(record.class_id)

      if (record.school_notification_files && record.school_notification_files.length > 0) {
        this.fileList = record.school_notification_files.map(file => ({
          uid: file.id,
          name: file.file.split('/').pop(),
          status: 'done',
          url: file.file,
        }))
      } else {
        this.fileList = []
      }
      this.filesToDelete = []

      this.$nextTick(() => {
        this.form.setFieldsValue({
          subject: record.subject,
          message: record.message,
        })
      })
    },
    getTemplates() {
      this.$http.get('/api/sms/templates').then(({ data }) => {
        this.templates = data.data
      })
    },
    getSmsBalance() {
      this.$http.get('/api/sms/balance').then(({ data }) => {
        this.smsBalance = data.data.balance || 0
      })
    },
    getClasses() {
      this.$http.get('/api/class', { params: { session_id: this.selectedSession } }).then(({ data }) => {
        this.schoolClasses = data.data
      })
    },
    getClassSections(classId) {
      this.$http.get(`/api/section/class/${classId}`).then(({ data }) => {
        this.sections = data.data
      })
    },
    handleEdit() {
      this.form.validateFields((err, values) => {
        if (!err) {
          this.$confirm({
            title: 'Update Notice?',
            content: 'Are you sure you want to update this notification?',
            onOk: () => {
              this.loading = true
              const payload = {
                subject: values.subject,
                message: values.message,
                student: this.stCheck ? 1 : 0,
                father: 0,
                mother: 0,
                guardian: 0,
                teacher: this.trCheck ? 1 : 0,
                sms: this.selectedTypes.includes('sms') ? 1 : 0,
                email: this.selectedTypes.includes('email') ? 1 : 0,
                push: this.selectedTypes.includes('push') ? 1 : 0,
                sms_template_id: this.selectedTemplate,
                files: this.fileList.filter(f => !f.status), // Only new files
                files_to_delete: this.filesToDelete,
              }
              const formData = window.jsonToFormData(payload)
              this.$http.post(`/api/notifications/${this.oldrecord.id}`, formData).then(() => {
                this.$emit('updateTable')
                this.visible = false
                this.$notification.success({ message: 'Success', description: 'Notification updated successfully' })
              }).finally(() => { this.loading = false })
            },
          })
        }
      })
    },
    handleCancel() {
      this.visible = false
      this.form.resetFields()
    },
    handleRemove(file) {
      if (file.status === 'done') {
        this.filesToDelete.push(file.uid)
      }
      this.fileList = this.fileList.filter(f => f.uid !== file.uid)
    },
    beforeUpload(file) {
      this.fileList = [...this.fileList, file]
      return false
    },
    getSessionName(id) {
      const s = find(this.schoolSessions, ['id', id])
      return s ? s.name : 'Unknown Session'
    },
    getClassName(id) {
      if (id === 0) return 'All Classes'
      const c = find(this.schoolClasses, ['id', id])
      return c ? c.name : 'Unknown Class'
    },
    getSectionName(id) {
      if (id === 0) return 'All Sections'
      const s = find(this.sections, ['id', id])
      return s ? s.name : 'Unknown Section'
    },
  },
}
</script>

<style scoped>
.notification-modal >>> .ant-modal-header {
  border-bottom: none;
  padding: 24px 24px 0;
}
.notification-modal >>> .ant-modal-body {
  padding-top: 12px;
}
.notification-modal >>> .ant-modal-footer {
  border-top: none;
  padding: 0 12px 24px;
}

.section-title {
  font-weight: 700;
  color: #1e293b;
  font-size: 1.1rem;
  margin-bottom: 1rem;
}

.delivery-checkboxes >>> .ant-checkbox-wrapper {
  font-weight: 500;
  margin-right: 24px;
}

.sms-balance-badge {
  background: #fef2f2;
  color: #991b1b;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
}

.balance-count {
  color: #ef4444;
}

.target-panel {
  background: #f8fafc;
  border-radius: 12px;
  height: 100%;
}

.audience-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
}

.opacity-75 {
  opacity: 0.85;
}

.attach-btn {
  border: 1px dashed #cbd5e1;
  background: #f8fafc;
  color: #475569;
  font-weight: 600;
}

.class-selection-area {
  border-color: #f1f5f9 !important;
}
</style>
